import { GetOptionsProps, InlinePopupProps, patternToRegExp } from "csi/middleware/inline/InlinePopup";
import { DelayedRequest, DelayedRequestState } from "csi/store/DelayedRequest";
import { StoreNames } from "csi/store/Names";
import { doesStoreUseSlimCard, getStoreSuggestedGroups, shouldGroupStoreItems } from "csi/store/common";
import { useEffectAsync } from "designSystem/hooks";
import { Button, MultiSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { getNoneComponentItem } from "designSystem/utilities/component-item-functions";
import { stopMuiEvent } from "designSystem/utilities/prevent-default-behavior";
import _ from "lodash";
import React, { FunctionComponent, KeyboardEventHandler, SyntheticEvent, useRef, useState } from "react";
import DropdownPlaceholder from "./DropdownPlaceholder";

/**
 * Add Select None option
 */
function addSelectNoneItem(options: ComponentItem[], itemNone: ComponentItem) {
	const emptyItemIndex = options.findIndex((item) => item.name == "");
	if (emptyItemIndex >= 0) {
		options.splice(emptyItemIndex, 1);
	}
	options.unshift(itemNone);
}

const InlineMultiSelect: FunctionComponent<InlinePopupProps> = (props) => {
	const { allowPinyin, dropdownOpen, getOptions, onCancel, onChange, onLastInputChange, placeholderOptions } = props;
	const [selectOptions, setSelectOptions] = useState<GetOptionsProps>(placeholderOptions);
	const { delaySearch, store } = selectOptions;
	const openState = selectOptions.open || false;
	const [delayedRequestState, setDelayedRequestState] = useState<DelayedRequestState>(DelayedRequestState.WAITING);

	const [requestSent, setRequestSent] = useState<boolean>(false);

	const inputTextRef = useRef("");
	let inputText = inputTextRef.current;

	// create item to deselect all
	const loc = useLocale();
	const itemNone = getNoneComponentItem(loc);

	let newValue: ComponentItem[];
	let sourceStore: StoreNames;

	if (store) {
		if ("bDelayedRequest" in store) {
			sourceStore = store.sourceStore;
		} else {
			sourceStore = store;
		}
	}
	const shouldGroupItems = shouldGroupStoreItems(sourceStore);
	const suggestedGroups = getStoreSuggestedGroups(sourceStore);
	const slimCard = doesStoreUseSlimCard(sourceStore);

	const onOpenCallback = async () => {
		setRequestSent(true);
	};

	const onCustomOption = (newOption: ComponentItem) => {
		const { options, value } = selectOptions;
		const newOptions = options.concat([newOption]);
		const newValue = value.concat([newOption]);
		setSelectOptions({ ...selectOptions, options: newOptions, value: newValue });
	};

	const onDelayedSearchStateChange = (state) => {
		if (state === DelayedRequestState.RESULTS_REFRESHED) {
			let options = (store as DelayedRequest).getResultsReact();
			const { value } = selectOptions;
			// options must always include value to avoid mui autocomplete exception
			if (value) {
				options = _.uniqBy(value.concat(options), (item) => item.id);
			}
			addSelectNoneItem(options, itemNone);
			setSelectOptions({ ...selectOptions, options: options, open: true });
		}
		setDelayedRequestState(state);
	};

	useEffectAsync(async () => {
		if (delaySearch) {
			const delayedStore: any = store;
			delayedStore.onChangeState = onDelayedSearchStateChange;
		}
		if (requestSent || dropdownOpen) {
			const optionsProps = delaySearch ? selectOptions : await getOptions(store);
			const { options } = optionsProps;
			addSelectNoneItem(options, itemNone);
			setSelectOptions({ ...optionsProps, options, open: true });
		}
	}, [requestSent]);

	// this event happen on every value change
	const onValueChange = (ev: SyntheticEvent, val: any): void => {
		newValue = val;
	};

	// this event only happen on dropdown closure
	const onChangeValue = (ev: SyntheticEvent, val: any): void => {
		onChange(ev, val?.map((item) => item.value));
	};

	// process TAB and Esc
	const onKeyDown: KeyboardEventHandler = (ev) => {
		const { key, target } = ev;

		if (key === "Tab") {
			// stop event to prevent focus shit, but call onChangeValue onKeyUp (below);
			stopMuiEvent(ev);
		} else if (key === "Escape") {
			stopMuiEvent(ev);
			onCancel();
		} else if ((key === "Enter") && delaySearch && openState && (target as HTMLElement).tagName === "INPUT") {
			stopMuiEvent(ev);
			onInputChange(null, null, "input");
		}
	};

	// TAB move editing to the next table cell, and if TAB click processed on key down, then KeyUp might be processed by next editing component 
	// so, disable onKeyDown tab processing and process keyUp for TAB here
	const onKeyUp: KeyboardEventHandler = (ev) => {
		const { key } = ev;

		if (key === "Tab") {
			stopMuiEvent(ev);
			onChangeValue(ev, newValue || selectOptions.value);
		}
	};

	const onInputChange = (event: React.SyntheticEvent, value: string, reason: string) => {
		// update reactLastInput on controller for user input
		// set it to "" for all other change reasons (example reason=="reset")
		onLastInputChange(reason === "input" ? value : "");
		
		if (delaySearch && (reason === "input")) {
			if (value === null) {
				// DelayedSearch and Enter clicked
				(store as DelayedRequest).skipDelayOnNextQuery = true;
			} else {
				inputText = inputTextRef.current = value;
			}
			const storeOptions = { ignoreCase: true };
			const qs = "*" + inputText + "*";
			const rs = patternToRegExp(qs);
			rs.toString = () => qs;
			const query = { Name: rs };
			getOptions(store, query, storeOptions);
		}
	};

	const actions = selectOptions.selectActions?.map((item, idx) => (
		<Button
			design="link"
			fullWidth
			hideOverflow
			key={idx}
			onClick={() => item.onAction(newValue?.map((item) => item.value))}
			res={item.res}
		>{item.label}</Button>
	));

	return (
		<MultiSelect
			DropdownPlaceholder={delaySearch && DropdownPlaceholder({ state: delayedRequestState, actions, onDialogClose: onCancel })}
			allowCustomOptions={selectOptions.allowCustomOptions}
			allowPinyin={allowPinyin}
			autoFocus
			clearOnBlur={false}
			delayedRequestHasResults={delaySearch && (delayedRequestState === DelayedRequestState.RESULTS)}
			disableClearable
			grouped={shouldGroupItems}
			id="csi-automation-inline-multiselect"
			inputValue={inputText}
			noChips
			noInputToggle
			onCancel={onCancel}
			onChange={onChangeValue}
			onCustomOption={onCustomOption}
			onInputChange={onInputChange}
			onKeyDown={onKeyDown}
			onKeyUp={onKeyUp}
			onOpenCallback={onOpenCallback}
			onValueChange={onValueChange}
			open={openState}
			options={selectOptions.options}
			placeHolderRes={selectOptions.placeholderRes || "Select"}
			selectActions={actions}
			selectOnFocus
			selectedFirst
			slimCard={slimCard}
			suggestedGroups={suggestedGroups}
			value={selectOptions.value}
		/>
	);
};

export default InlineMultiSelect;
